import { VariantOptionProps } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * VariantOption renders a single product variant option as either:
 * 1. A color swatch (with optional image)
 * 2. An image thumbnail
 * 3. A text button (default)
 */
export const VariantOption: React.FC<VariantOptionProps> = ({
    value,
    isSelected,
    isAvailable,
    onClick,
    type,
    color,
    imageUrl,
    variant,
    showVariantImagesAsColors,
}) => {
    // Common styles for all variant options
    const swatchClassName = cn(
        'size-12 rounded-md bg-center bg-cover relative',
        isAvailable
            ? 'opacity-100 cursor-pointer'
            : 'opacity-40 cursor-not-allowed',
        isSelected ? 'border-2 border-foreground' : 'border border-border',
    )
    const onSwatchClick = isAvailable ? onClick : undefined

    if (type === 'color') {
        // 1. Color swatch value
        if (!!imageUrl) {
            return (
                <div
                    className={swatchClassName}
                    onClick={onSwatchClick}
                    style={{ backgroundImage: `url(${imageUrl})` }}
                    aria-label={`Color variant: ${value}`}
                />
            )
        }

        // 2. Simple color swatch
        if (!!color) {
            return (
                <div
                    className={swatchClassName}
                    onClick={onSwatchClick}
                    style={{ backgroundColor: color }}
                    aria-label={`Color: ${value}`}
                />
            )
        }

        // 3. Fall back on the variant.image if showVariantImagesAsColors is enabled
        if (showVariantImagesAsColors && variant?.featuredImage) {
            return (
                <div
                    className={swatchClassName}
                    onClick={onSwatchClick}
                    style={{
                        backgroundImage: `url(${variant.featuredImage})`,
                    }}
                    aria-label={`Color variant: ${value}`}
                />
            )
        }
    }

    // 4. Image thumbnail
    if (type === 'image' && imageUrl) {
        return (
            <div
                className={swatchClassName}
                onClick={onSwatchClick}
                style={{ backgroundImage: `url(${imageUrl})` }}
                aria-label={`Image variant: ${value}`}
            />
        )
    }

    // 5. Default text button
    return (
        <Button
            size="sm"
            variant={isSelected ? 'default' : 'outline'}
            disabled={!isAvailable}
            onClick={onClick}
            className={cn(
                'min-w-[44px] min-h-[32px] px-3 text-[11px]',
                isSelected ? 'font-medium' : 'font-normal',
            )}
        >
            {value}
        </Button>
    )
}
